/** @jsxImportSource @emotion/react */
import React from 'react'
import {
  HomeTeam,
  MyPlayer,
  MyTeamOfficial,
  UpcomingGame,
  VisitingTeam,
} from '@sportninja/common/types/PlayerAttendance'
import { teamSectionContainerStyle } from '../styles'
import TeamSectionHeader from './TeamSectionHeader'
import AttendanceQuestion from './AttendanceQuestion'
import AttendanceCounter from './AttendanceCounter'
import ManageTeamAttendance from './ManageTeamAttendance'
import RoleButtonsRow from './RoleButtonsRow'
import SnacksInfo from './SnacksInfo'
import VideographerInfo from './VideographerInfo'
import { disableLivestream } from '@sportninja/common/utils/utils'

export interface TeamSectionProps {
  team: HomeTeam | VisitingTeam
  teamSide: 'home' | 'visitor'
  showHeader: boolean
  playersAttendance: MyPlayer[]
  teamRep: MyTeamOfficial | null
  rowItem: UpcomingGame
  onAttendanceSelect: (params: {
    playerId: string
    gameId: string
    rosterId: string
    status: number
  }) => void
  onPressManageAttendance: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    showWaiver: boolean,
    isReadOnly: boolean,
    ownPlayerIds: string[]
  ) => void
  onPressManageRefreshments: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    showWaiver: boolean
  ) => void
  onPressManageVideographer?: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    teamSide: 'home' | 'visitor'
  ) => void
}

const TeamSection: React.FC<TeamSectionProps> = ({
  team,
  teamSide,
  showHeader,
  playersAttendance,
  teamRep,
  rowItem,
  onAttendanceSelect,
  onPressManageAttendance,
  onPressManageRefreshments,
  onPressManageVideographer,
}) => {
  const isTeamRepOnTeam = !!teamRep
  const hasPlayers = playersAttendance && playersAttendance.length > 0
  const hasCounter = !!team?.attendance_numbers
  const showVideo = !!rowItem?.show_video && !!onPressManageVideographer

  return (
    <div css={teamSectionContainerStyle}>
      {showHeader ? <TeamSectionHeader teamName={team?.name ?? ''} /> : null}

      {hasPlayers ? (
        <AttendanceQuestion
          playersAttendance={playersAttendance}
          gameId={rowItem.id}
          onAttendanceSelect={onAttendanceSelect}
        />
      ) : null}

      {hasCounter ? (
        <AttendanceCounter attendanceNumbers={team.attendance_numbers} />
      ) : null}

      {/* Everyone on the team sees it; only a team official can edit. */}
      <ManageTeamAttendance
        team={team}
        teamRep={teamRep}
        rowItem={rowItem}
        teamSide={teamSide}
        onPressManageAttendance={onPressManageAttendance}
      />

      <RoleButtonsRow
        snacks={
          <SnacksInfo
            isTeamRepOnTeam={isTeamRepOnTeam}
            team={team}
            onPressManageRefreshments={onPressManageRefreshments}
            gameId={rowItem.id}
            showWaiver={rowItem?.show_waiver}
            teamSide={teamSide}
          />
        }
        videographer={
          showVideo && !disableLivestream && onPressManageVideographer ? (
            <VideographerInfo
              isTeamRepOnTeam={isTeamRepOnTeam}
              team={team}
              onPressManageVideographer={onPressManageVideographer}
              gameId={rowItem.id}
              showVideo={showVideo}
              teamSide={teamSide}
            />
          ) : null
        }
      />
    </div>
  )
}

export default React.memo(TeamSection)
